import ProgramSummaryCalculator, {
  SummaryCalculator,
} from './ProgramSummaryCalculator';
import ProgramScenario, { PlainProgramScenario } from './Scenario';
import Worksheet, { PlainWorksheet } from './Worksheet';

export default class TotalProgramSummaryCalculator
  implements SummaryCalculator {
  private worksheets: Worksheet[] | PlainWorksheet[];

  private scenario: ProgramScenario | PlainProgramScenario;

  circulationUSF = 0;

  circulationPercentage = 0;

  additionalUSFForPlanningFactor = 0;

  subtotalProgramWithCirculationUSF = 0;

  totalProgramUSF = 0;

  deltaProgramUSF = 0;

  subtotalMultiplierForCirculation = 0;

  planningFactorPercentage = 0;

  rsfLoadFactorPercentage = 0;

  rentableLoadFactor = 0;

  totalProgramRSF = 0;

  deltaProgramRSF = 0;

  subtotalProgramUSF = 0;

  private linkedWorksheetsCalcs: SummaryCalculator[] = [];

  constructor(
    scenario: ProgramScenario | PlainProgramScenario,
    worksheets: Worksheet[] | PlainWorksheet[]
  ) {
    this.scenario = scenario;
    this.worksheets = worksheets;
    if (scenario && worksheets) this.calculate();
  }

  calculate = () => {
    this.worksheets.forEach((ws) => {
      const worksheetProgramSummary = new ProgramSummaryCalculator(
        ws.scenario,
        ws
      );
      this.linkedWorksheetsCalcs.push(worksheetProgramSummary);
      this.circulationUSF += worksheetProgramSummary.circulationUSF;
      this.additionalUSFForPlanningFactor +=
        worksheetProgramSummary.additionalUSFForPlanningFactor;
      this.subtotalProgramWithCirculationUSF +=
        worksheetProgramSummary.subtotalProgramWithCirculationUSF;
      this.totalProgramUSF += worksheetProgramSummary.totalProgramUSF;
      this.subtotalProgramUSF += ws.subtotalProgramUSF;
    });

    this.deltaProgramUSF =
      this.scenario.totalAvailableUSF - this.totalProgramUSF;

    this.subtotalMultiplierForCirculation = this.calculateSubtotalMultiplierForCirculation();
    this.circulationPercentage = this.calculateCirculationPercentage();
    this.planningFactorPercentage = this.calculatePlanningFactorPercentage();

    // All project scenarios will have the same load factor, no need for a weighted calc
    this.rsfLoadFactorPercentage = this.scenario.rsfLoadFactorPercentage;
    this.rentableLoadFactor =
      this.totalProgramUSF * (this.rsfLoadFactorPercentage / 100);
    this.totalProgramRSF = this.totalProgramUSF + this.rentableLoadFactor;
    this.deltaProgramRSF =
      this.scenario.totalAvailableRSF - this.totalProgramRSF;
  };

  calculateSubtotalMultiplierForCirculation = () => {
    if (this.subtotalProgramUSF === 0)
      return this.calculateAverage(
        this.linkedWorksheetsCalcs.map(
          (c) => c.subtotalMultiplierForCirculation
        )
      );
    return this.circulationUSF / this.subtotalProgramUSF || 0;
  };

  private calculateCirculationPercentage = () => {
    return (
      (this.subtotalMultiplierForCirculation * 100) /
        (1 + this.subtotalMultiplierForCirculation) || 0
    );
  };

  private calculatePlanningFactorPercentage = () => {
    if (this.subtotalProgramUSF === 0)
      return this.calculateAverage(
        this.linkedWorksheetsCalcs.map((c) => c.planningFactorPercentage)
      );
    return (
      (this.additionalUSFForPlanningFactor /
        this.subtotalProgramWithCirculationUSF) *
        100 || 0
    );
  };

  private calculateAverage = (numbers: number[]) => {
    return numbers.reduce((acc, num) => acc + num, 0) / numbers.length;
  };

  toPlainObject = () => ({
    circulationUSF: this.circulationUSF,
    circulationPercentage: this.circulationPercentage,
    additionalUSFForPlanningFactor: this.additionalUSFForPlanningFactor,
    subtotalProgramWithCirculationUSF: this.subtotalProgramWithCirculationUSF,
    totalProgramUSF: this.totalProgramUSF,
    deltaProgramUSF: this.deltaProgramUSF,
    planningFactorPercentage: this.planningFactorPercentage,
    rsfLoadFactorPercentage: this.rsfLoadFactorPercentage,
    rentableLoadFactor: this.rentableLoadFactor,
    totalProgramRSF: this.totalProgramRSF,
    deltaProgramRSF: this.deltaProgramRSF,
    subtotalProgramUSF: this.subtotalProgramUSF,
    subtotalMultiplierForCirculation: this.subtotalMultiplierForCirculation,
  });
}
